import { Controller } from "@hotwired/stimulus"

function formatMass(kg: number): string {
  if (kg >= 1_000) return `${(kg / 1_000).toFixed(1)} t`
  return `${kg.toFixed(0)} kg`
}

function formatThrust(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MN`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)} kN`
  return `${n.toFixed(0)} N`
}

function formatDeltaV(ms: number): string {
  if (ms >= 1_000_000) return `${(ms / 1_000).toFixed(0)} km/s`
  if (ms >= 10_000) return `${(ms / 1_000).toFixed(1)} km/s`
  return `${ms.toFixed(0)} m/s`
}

export default class ShipPickerController extends Controller {
  static values = { playPath: String }
  static targets = ["tab", "panel", "card", "stats", "launchLink"]

  declare playPathValue: string
  declare tabTargets: HTMLElement[]
  declare panelTargets: HTMLElement[]
  declare cardTargets: HTMLElement[]
  declare statsTarget: HTMLElement
  declare launchLinkTarget: HTMLAnchorElement
  declare hasLaunchLinkTarget: boolean

  connect() {
    const first = this.cardTargets.find(c => !c.closest(".hidden"))
    if (first) {
      first.classList.add("ship-card-active")
      this.showStats(first)
      this.updateLaunchLink(first.dataset.slug!)
    }
  }

  switchTab(event: Event) {
    const tab = event.currentTarget as HTMLElement
    const engineType = tab.dataset.engineType!

    for (const t of this.tabTargets) t.classList.remove("ship-tab-active")
    tab.classList.add("ship-tab-active")

    for (const p of this.panelTargets) {
      p.classList.toggle("hidden", p.dataset.engineType !== engineType)
    }

    // Select first ship in the new tab if none is active there
    const panel = this.panelTargets.find(p => p.dataset.engineType === engineType)!
    const cards = this.cardsInPanel(panel)
    const hasActive = cards.some(c => c.classList.contains("ship-card-active"))
    if (!hasActive && cards.length) {
      // Clear all active states first
      for (const c of this.cardTargets) c.classList.remove("ship-card-active")
      cards[0].classList.add("ship-card-active")
      this.showStats(cards[0])
      this.updateLaunchLink(cards[0].dataset.slug!)
    }
  }

  select(event: Event) {
    const card = event.currentTarget as HTMLElement

    for (const c of this.cardTargets) c.classList.remove("ship-card-active")
    card.classList.add("ship-card-active")

    this.showStats(card)
    this.updateLaunchLink(card.dataset.slug!)
  }

  private cardsInPanel(panel: HTMLElement): HTMLElement[] {
    return this.cardTargets.filter(c => panel.contains(c))
  }

  private showStats(card: HTMLElement) {
    const d = card.dataset
    const dryMass = parseFloat(d.dryMass || "0")
    const fuel = parseFloat(d.fuel || "0")
    const totalMass = parseFloat(d.totalMass || "0")
    const thrust = parseFloat(d.thrust || "0")
    const isp = parseFloat(d.isp || "0")
    const deltaV = parseFloat(d.deltaV || "0")
    const accel = totalMass > 0 ? thrust / totalMass : 0

    this.statsTarget.innerHTML = `
      <div class="ship-stats-header">
        <h3 class="ship-stats-name">${d.name}</h3>
        <span class="ship-stats-class">${d.shipClass} &middot; ${d.engineType}</span>
      </div>
      <div class="ship-stats-grid">
        <div class="ship-stat">
          <span class="ship-stat-label">Dry Mass</span>
          <span class="ship-stat-value">${formatMass(dryMass)}</span>
        </div>
        <div class="ship-stat">
          <span class="ship-stat-label">Fuel</span>
          <span class="ship-stat-value">${fuel ? formatMass(fuel) : "None"}</span>
        </div>
        <div class="ship-stat">
          <span class="ship-stat-label">Total Mass</span>
          <span class="ship-stat-value">${formatMass(totalMass)}</span>
        </div>
        <div class="ship-stat">
          <span class="ship-stat-label">Thrust</span>
          <span class="ship-stat-value">${formatThrust(thrust)}</span>
        </div>
        <div class="ship-stat">
          <span class="ship-stat-label">ISP</span>
          <span class="ship-stat-value">${isp ? `${isp.toFixed(0)} s` : "N/A"}</span>
        </div>
        <div class="ship-stat">
          <span class="ship-stat-label">&Delta;V</span>
          <span class="ship-stat-value">${deltaV ? formatDeltaV(deltaV) : "&infin;"}</span>
        </div>
        ${d.engineName ? `
        <div class="ship-stat ship-stat-wide">
          <span class="ship-stat-label">Engine</span>
          <span class="ship-stat-value">${d.engineName}</span>
        </div>
        ` : ""}
        <div class="ship-stat">
          <span class="ship-stat-label">Accel</span>
          <span class="ship-stat-value">${accel > 100 ? `${(accel / 9.80665).toFixed(0)}g` : accel >= 0.01 ? `${accel.toFixed(2)} m/s&sup2;` : `${(accel * 1000).toFixed(2)} mm/s&sup2;`}</span>
        </div>
      </div>
    `
  }

  private updateLaunchLink(slug: string) {
    if (!this.hasLaunchLinkTarget) return
    const base = this.playPathValue
    const sep = base.includes("?") ? "&" : "?"
    this.launchLinkTarget.href = `${base}${sep}ship=${slug}`
  }
}
